import React from "react";
import { X, Check } from "lucide-react";
import { inr } from "@/lib/api";

const OVERSIZE_KEYS = ["3XL", "4XL", "5XL"];

/**
 * Final review modal shown before creating a Quote or Order. Displays customer,
 * every line item with breakdown, the GST slab table and the grand total.
 * The parent controls open/close and provides a confirm callback.
 */
export default function FinalReview({
  open,
  onClose,
  onConfirm,
  title,
  submittingLabel,
  customerName,
  customerPhone,
  items,
  totals,
  extraRows,          // e.g. [{ label: "Expected delivery", value: "…" }]
  advanceAmount,      // optional
  disableConfirm,
  submitting,
}) {
  if (!open) return null;
  const balance = advanceAmount != null ? (Number(totals.total) - Number(advanceAmount || 0)) : null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="card-surface w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="final-review">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EAE5DB]">
          <div>
            <div className="label-tiny">Final review — please confirm</div>
            <h3 className="font-display font-bold text-xl">{title}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[#F6F1E7] rounded" data-testid="final-review-close">
            <X size={16} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <div className="label-tiny mb-1">Customer</div>
            <div className="font-medium">{customerName || "—"}</div>
            {customerPhone && <div className="text-xs text-[#75706A] mono">{customerPhone}</div>}
          </div>

          {(extraRows || []).map((r, i) => (
            <div key={i}>
              <div className="label-tiny mb-1">{r.label}</div>
              <div className="text-sm">{r.value}</div>
            </div>
          ))}

          <div>
            <div className="label-tiny mb-2">Line items ({items.length})</div>
            <div className="space-y-3">
              {items.map((it, i) => {
                const cust = Number(it.customization_price) || 0;
                const other = Number(it.other_customization_cost) || 0;
                const over = Number(it.oversize_surcharge) || 0;
                const overCount = OVERSIZE_KEYS.reduce((a, k) => a + Number((it.sizes || {})[k] || 0), 0);
                const line = Number(it.qty) * (Number(it.unit_price) + cust + other) + over * overCount;
                return (
                  <div key={i} className="p-3 rounded-md bg-[#FBF8F1] border border-[#EAE5DB] text-sm">
                    <div className="flex justify-between items-baseline gap-2 flex-wrap">
                      <div className="font-medium">{it.product || "(no product)"} {it.color ? <span className="text-[#75706A]"> · {it.color}</span> : null}</div>
                      <div className="mono font-bold">{inr(line)}</div>
                    </div>
                    <div className="text-xs text-[#75706A] mt-1 space-y-0.5">
                      <div>Qty <b>{it.qty || 0}</b> × Unit {inr(it.unit_price || 0)}
                        {cust > 0 && <> + Cust {inr(cust)}/pc</>}
                        {other > 0 && <> + Other {inr(other)}/pc</>}
                      </div>
                      {Object.keys(it.sizes || {}).length > 0 && (
                        <div>Sizes: {Object.entries(it.sizes).map(([s, n]) => `${s}·${n}`).join("  ")}</div>
                      )}
                      {it.other_size && <div>Other size: {it.other_size}</div>}
                      {it.customization && <div>Customization: {it.customization}</div>}
                      {it.other_customization && <div>Other customization: {it.other_customization}</div>}
                      {over > 0 && overCount > 0 && <div>3XL+ surcharge: {inr(over)} × {overCount} pcs = <b>{inr(over * overCount)}</b></div>}
                      <div>HSN: {it.hsn_code || "—"} · GST @ {it.gst_percent ?? 5}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals */}
          <div className="p-4 rounded-md bg-[#F6F1E7] border border-[#EAE5DB] space-y-1">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span className="mono">{inr(totals.sub)}</span></div>
            {(totals.breakup || []).map(b => (
              <div key={b.slab} className="flex justify-between text-sm text-[#75706A]"><span>GST @ {b.slab}% on {inr(b.subtotal)}</span><span className="mono">{inr(b.gst)}</span></div>
            ))}
            <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-[#EAE5DB]"><span>Grand total</span><span className="mono">{inr(totals.total)}</span></div>
            {advanceAmount != null && (
              <>
                <div className="flex justify-between text-sm pt-1"><span>Advance received</span><span className="mono text-[#4A7258]">{inr(advanceAmount)}</span></div>
                <div className="flex justify-between text-sm font-medium">
                  <span>{Number(advanceAmount) > 0 ? "Balance due" : "Full balance due"}</span>
                  <span className={`mono ${balance > 0 ? "text-[#B83A3A]" : "text-[#4A7258]"}`}>{inr(balance)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#EAE5DB] px-5 py-3 flex items-center justify-between gap-2">
          <button type="button" onClick={onClose} className="btn-ghost text-sm" data-testid="final-review-back">
            ← Back to edit
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={disableConfirm || submitting}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
            data-testid="final-review-confirm"
          >
            <Check size={14} />
            {submitting ? "Saving…" : (submittingLabel || "Confirm & create")}
          </button>
        </div>
      </div>
    </div>
  );
}
